/**
 * Query Key Factories
 *
 * Centralized query keys for type-safe cache management.
 * Each domain has a factory with hierarchical keys for efficient invalidation.
 *
 * Usage:
 * ```tsx
 * // In a query
 * queryKey: processKeys.detail('123')
 *
 * // Invalidate all processes
 * queryClient.invalidateQueries({ queryKey: processKeys.all })
 *
 * // Invalidate all process lists
 * queryClient.invalidateQueries({ queryKey: processKeys.lists() })
 *
 * // Invalidate specific process
 * queryClient.invalidateQueries({ queryKey: processKeys.detail('123') })
 * ```
 */

import type { ProcessLayoutScope } from '@bliks-io/contracts';

export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...userKeys.lists(), filters] as const,
  details: () => [...userKeys.all, 'detail'] as const,
  detail: (id: string) => [...userKeys.details(), id] as const,
  profile: () => [...userKeys.all, 'profile'] as const,
  security: () => [...userKeys.all, 'security'] as const
};

export const departmentKeys = {
  all: ['departments'] as const,
  lists: () => [...departmentKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) => [...departmentKeys.lists(), filters || {}] as const,
  details: () => [...departmentKeys.all, 'detail'] as const,
  detail: (id: string) => [...departmentKeys.details(), id] as const
};

export const orgFunctionKeys = {
  all: ['orgFunctions'] as const,
  lists: () => [...orgFunctionKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) => [...orgFunctionKeys.lists(), filters || {}] as const,
  details: () => [...orgFunctionKeys.all, 'detail'] as const,
  detail: (id: string) => [...orgFunctionKeys.details(), id] as const,
  members: (id: string) => [...orgFunctionKeys.detail(id), 'members'] as const,
  mine: () => [...orgFunctionKeys.all, 'mine'] as const
};

export const organizationKeys = {
  all: ['organizations'] as const,
  lists: () => [...organizationKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...organizationKeys.lists(), filters || {}] as const,
  details: () => [...organizationKeys.all, 'detail'] as const,
  detail: (id: string) => [...organizationKeys.details(), id] as const,
  settings: () => [...organizationKeys.all, 'settings'] as const
};

export const authKeys = {
  all: ['auth'] as const,
  profile: () => [...authKeys.all, 'profile'] as const,
  session: () => [...authKeys.all, 'session'] as const,
  memberships: () => [...authKeys.all, 'memberships'] as const
};

export const systemKeys = {
  all: ['systems'] as const,
  lists: () => [...systemKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) => [...systemKeys.lists(), filters || {}] as const,
  details: () => [...systemKeys.all, 'detail'] as const,
  detail: (id: string) => [...systemKeys.details(), id] as const,
  landscape: () => [...systemKeys.all, 'landscape'] as const
};

export const artifactKeys = {
  all: ['artifacts'] as const,
  lists: () => [...artifactKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) => [...artifactKeys.lists(), filters || {}] as const,
  details: () => [...artifactKeys.all, 'detail'] as const,
  detail: (id: string) => [...artifactKeys.details(), id] as const
};

export const externalContactKeys = {
  all: ['external-contacts'] as const,
  lists: () => [...externalContactKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...externalContactKeys.lists(), filters || {}] as const,
  details: () => [...externalContactKeys.all, 'detail'] as const,
  detail: (id: string) => [...externalContactKeys.details(), id] as const
};

export const partnerMaterialKeys = {
  all: ['partner-materials'] as const,
  admin: () => [...partnerMaterialKeys.all, 'admin'] as const,
  partner: () => [...partnerMaterialKeys.all, 'partner'] as const
};

export const partnerNewsKeys = {
  all: ['partner-news'] as const,
  admin: () => [...partnerNewsKeys.all, 'admin'] as const,
  partner: () => [...partnerNewsKeys.all, 'partner'] as const
};

export const orgMemberKeys = {
  all: ['org-members'] as const,
  lists: () => [...orgMemberKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) => [...orgMemberKeys.lists(), filters || {}] as const,
  details: () => [...orgMemberKeys.all, 'detail'] as const,
  detail: (id: string) => [...orgMemberKeys.details(), id] as const,
  /** Mutation key prefix shared by the member row actions. */
  rowActionMutations: () => [...orgMemberKeys.all, 'row-action-mutation'] as const,
  sendInvitationMutation: () => [...orgMemberKeys.rowActionMutations(), 'send-invitation'] as const,
  suspendMutation: () => [...orgMemberKeys.rowActionMutations(), 'suspend'] as const,
  unsuspendMutation: () => [...orgMemberKeys.rowActionMutations(), 'unsuspend'] as const,
  activateSalesMutation: () => [...orgMemberKeys.rowActionMutations(), 'activate-sales'] as const
};

export const processKeys = {
  all: ['processes'] as const,
  lists: () => [...processKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) => [...processKeys.lists(), filters || {}] as const,
  details: () => [...processKeys.all, 'detail'] as const,
  detail: (id: string) => [...processKeys.details(), id] as const,
  flowLayout: (id: string, updatedAt?: string, stepCount?: number, fingerprint?: string) =>
    [...processKeys.detail(id), 'flowLayout', updatedAt, stepCount, fingerprint] as const,
  // TODO: Review AI Code for feature: Process Map
  map: () => [...processKeys.all, 'map'] as const,
  connections: (id: string) => [...processKeys.all, 'connections', id] as const
};

export const recordingKeys = {
  all: ['recordings'] as const,
  lists: () => [...recordingKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) => [...recordingKeys.lists(), filters || {}] as const,
  details: () => [...recordingKeys.all, 'detail'] as const,
  detail: (id: string) => [...recordingKeys.details(), id] as const,
  stepRecordings: () => [...recordingKeys.all, 'step-recordings'] as const,
  stepRecording: (id: string) => [...recordingKeys.stepRecordings(), id] as const
};

export const surveyKeys = {
  all: ['surveys'] as const,
  lists: () => [...surveyKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) => [...surveyKeys.lists(), filters || {}] as const,
  pending: () => [...surveyKeys.lists(), 'pending'] as const,
  details: () => [...surveyKeys.all, 'detail'] as const,
  detail: (id: string) => [...surveyKeys.details(), id] as const
};

export const initializeKeys = {
  all: ['initialize'] as const,
  unresolvedDepartments: () => [...initializeKeys.all, 'unresolved-departments'] as const,
  summary: () => [...initializeKeys.all, 'summary'] as const
};

export const dataSyncKeys = {
  all: ['data-sync'] as const,
  status: () => [...dataSyncKeys.all, 'status'] as const,
  syncRun: (id: string) => [...dataSyncKeys.all, 'sync-run', id] as const,
  setupRows: () => [...dataSyncKeys.all, 'setup-rows'] as const,
  linkPersonio: () => [...dataSyncKeys.all, 'link-personio'] as const,
  readPersonioForSetup: () => [...dataSyncKeys.all, 'read-personio-for-setup'] as const
};

export const bliksyKeys = {
  all: ['bliksy'] as const,
  sessions: () => [...bliksyKeys.all, 'sessions'] as const,
  session: (id: string) => [...bliksyKeys.sessions(), id] as const,
  chatHistory: (sessionId: string) => [...bliksyKeys.all, 'chat-history', sessionId] as const
};

export const tenantOrganizationKeys = {
  all: ['tenant-organizations'] as const,
  detail: (tenantId: string) => [...tenantOrganizationKeys.all, tenantId] as const
};

// TODO: Review AI Code for feature: Process Map
export const processChainKeys = {
  all: ['process-chains'] as const,
  lists: () => [...processChainKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...processChainKeys.lists(), filters || {}] as const,
  details: () => [...processChainKeys.all, 'detail'] as const,
  detail: (id: string) => [...processChainKeys.details(), id] as const,
  tree: () => [...processChainKeys.all, 'tree'] as const
};

export const appKeys = {
  all: ['apps'] as const,
  installed: () => [...appKeys.all, 'installed'] as const,
  // Admin management list: installed apps including the disabled ones.
  installedAll: () => [...appKeys.installed(), 'all'] as const,
  registry: () => [...appKeys.all, 'registry'] as const,
  tenantApps: (tenantId: string) => [...appKeys.all, 'tenant', tenantId] as const,
  /** Mutation key shared by the activation toggles of the admin management list. */
  toggleMutations: () => [...appKeys.all, 'toggle-mutation'] as const,
  /** Mutation key shared by the optimistic writes to one tenant's apps list. */
  tenantAppWriteMutations: (tenantId: string) =>
    [...appKeys.all, 'tenant-write-mutation', tenantId] as const,
  /** Mutation key of the removals from one tenant's apps list. */
  tenantAppLockMutations: (tenantId: string) =>
    [...appKeys.all, 'tenant-lock-mutation', tenantId] as const
};

export const platformAiSettingsKeys = {
  all: ['platform-ai-settings'] as const
};

export const appVersionKeys = {
  all: ['app-version'] as const
};

export const supportTicketKeys = {
  all: ['support-tickets'] as const,
  lists: () => [...supportTicketKeys.all, 'list'] as const,
  detail: (ticketId: number) => [...supportTicketKeys.all, 'detail', ticketId] as const,
  attachment: (ticketId: number, messageId: number, attachmentId: number) =>
    [...supportTicketKeys.detail(ticketId), 'attachment', messageId, attachmentId] as const
};

export const dashboardKeys = {
  all: ['dashboard'] as const
};

export const recordingDashboardKeys = {
  all: ['recording-dashboard'] as const
};

export const auditLogKeys = {
  all: ['superadmin-audit-logs'] as const,
  lists: () => [...auditLogKeys.all, 'list'] as const,
  list: (filters?: object) => [...auditLogKeys.lists(), filters ?? {}] as const
};

export const recordingAnalyticsKeys = {
  all: ['superadmin-recording-analytics'] as const,
  lists: () => [...recordingAnalyticsKeys.all, 'list'] as const,
  list: (filters?: object) => [...recordingAnalyticsKeys.lists(), filters ?? {}] as const
};

export const aiUsageKeys = {
  all: ['superadmin-ai-usage'] as const,
  lists: () => [...aiUsageKeys.all, 'list'] as const,
  list: (filters?: object) => [...aiUsageKeys.lists(), filters ?? {}] as const
};

export const processDraftKeys = {
  all: ['process-drafts'] as const,
  detail: (processId: string) => [...processDraftKeys.all, processId] as const,
  /** Mutation key of every write to the process's draft. */
  mutations: (processId: string) => [...processDraftKeys.all, processId, 'mutation'] as const
};

export const processLayoutKeys = {
  all: ['process-layouts'] as const,
  detail: (processId: string, scope: ProcessLayoutScope) =>
    [...processLayoutKeys.all, processId, scope] as const
};

export const stepTemplateKeys = {
  all: ['step-templates'] as const,
  list: () => [...stepTemplateKeys.all, 'list'] as const
};

export const seatUsageKeys = {
  all: ['seat-usage'] as const,
  tenant: (tenantId: string) => [...seatUsageKeys.all, 'tenant', tenantId] as const,
  organization: () => [...seatUsageKeys.all, 'organization'] as const
};

export const pickerSuggestionsKeys = {
  all: ['picker-suggestions'] as const,
  lists: () => [...pickerSuggestionsKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...pickerSuggestionsKeys.lists(), filters] as const
};

/** Freigaben: one request (GET /approval/:id) and the list (GET /approvals). */
export const approvalKeys = {
  all: ['approvals'] as const,
  list: ['approvals', 'list'] as const,
  detail: (id: string) => ['approvals', 'detail', id] as const
};

export const taskKeys = {
  all: ['tasks'] as const,
  /** The caller's open Aufgaben (GET /task/mine); a prefix match of `all`, so one invalidation refreshes both. */
  mine: ['tasks', 'mine'] as const
};

export const entitySuggestionKeys = {
  all: ['entity-suggestion'] as const,
  detail: (id: string | undefined) => [...entitySuggestionKeys.all, id] as const,
  approverList: () => [...entitySuggestionKeys.all, 'approver-list'] as const,
  shadowSystems: () => [...entitySuggestionKeys.all, 'shadow-systems'] as const,
  duplicateCandidates: (filters: Record<string, unknown>) =>
    [...entitySuggestionKeys.all, 'duplicate-candidates', filters] as const
};

export const mergeCandidateKeys = {
  all: ['merge-candidates'] as const,
  detail: (assignmentId: string | undefined) => [...mergeCandidateKeys.all, assignmentId] as const
};

/** Super-admin preview of a HubSpot company (basics + licences + status). */
export const hubspotCompanyKeys = {
  all: ['admin', 'hubspot-company'] as const,
  detail: (companyId: string) => [...hubspotCompanyKeys.all, companyId] as const
};
